import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { merge, Observable, Subject } from 'rxjs';
import { Action } from '../../../models/action';
import { environment } from '../../../../environments/environment';
import { catchError, filter, map, takeWhile, tap } from 'rxjs/operators';
import { GUID } from '../../../utils/guid';
import { ActionStatusEnum } from '../../../enum/action-status.enum';
import { MessagingService } from './messaging.service';
import { journalInitialize, journalUpdate } from '../../../store/journal/journal.actions';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';
import { Clock } from '../../../utils/clock';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
    providedIn: 'root'
})
export class ActionService {

    private channels = {};
    public readonly updates$ = new Subject<Action>();

    constructor(private http: HttpClient, private snackbar: MatSnackBar,
                private messagingService: MessagingService, private store: Store<AppState>) {
    }

    updateReceived(action: Action): void {
        this.updates$.next(action);
    }

    initialize(): void {
        this.getRecent().subscribe(entries => this.store.dispatch(journalInitialize({ entries })));
    }

    getRecent(): Observable<Action[]> {
        const headers = new HttpHeaders().set('Accept', 'application/ld+json');
        return this.http.get<any>(`${environment.apiUrl}/actions`, { headers }).pipe(
            map((result) => result['hydra:member']),
            catchError(() => {
                this.snackbar.open($localize`Unable to fetch your journal.  Log in again to retry.`);
                return [];
            })
        );
    }

    dispatch(action: Action): Observable<Action | null> {
        action.createdAt = Clock.now();
        action.updatedAt = Clock.now();
        this.store.dispatch(journalUpdate({ action }));
        return this.http.post<Action>(`${environment.apiUrl}/actions`, action).pipe(
            tap(result => this.store.dispatch(journalUpdate({ action: result }))),
            catchError((error) => {
                this.snackbar.open($localize`Unable to communicate with the server.  Please try again later.`);
                const errorAction = { ...action };
                errorAction.status = ActionStatusEnum.ERROR;
                errorAction.errors = error;
                this.store.dispatch(journalUpdate({ action: errorAction }));
                throw error;
            })
        );
    }

    progress(channel: string): Observable<Action> {
        return this.updates$.pipe(
            filter(action => action.channel === channel),
            takeWhile(action => action.status !== ActionStatusEnum.COMPLETED && action.status !== ActionStatusEnum.ERROR, true),
            filter(action => action.progress === -1 || action.progress >= this.channels[channel].progress),
            tap((action) => this.channels[channel] = action)
        );
    }

    sync(actionId: string, entityIds: string[] = [], parameters: any = null): Observable<Action> {
        const channel = GUID.create();
        const action: Action = {
            actionId, entityIds, parameters, async: false, channel,
            status: ActionStatusEnum.PENDING, progress: 0
        };
        this.channels[channel] = action;
        return merge(this.dispatch(action), this.progress(channel));
    }

    async(actionId: string, entityIds: string[] = [], parameters: any = null): Observable<Action> {
        const channel = GUID.create();
        const action: Action = {
            actionId, entityIds, parameters, async: true, channel,
            status: ActionStatusEnum.PENDING, progress: 0
        };
        this.channels[channel] = action;
        return merge(this.dispatch(action), this.progress(channel));
    }
}
